import React, { useEffect, useState } from 'react'
import { useSearchDealsByCompanyQuery, useSearchDealsQuery } from '@paintscout/api'
import { makeStyles } from '@material-ui/core'
import type { Deal } from '@paintscout/api'
import { useDebouncedCallback } from '@paintscout/ui/hooks'
import Typography from '@paintscout/ui/components/Typography'
import type { DropdownSelectOption, DropdownSelectProps, MultiSelectProps, SingleSelectProps } from '../DropdownSelect'
import DropdownSelect from '../DropdownSelect'
import { useCrmDialogs } from '../../providers/DialogStack'

const useStyles = makeStyles({
  error: {
    '& a': {
      color: 'inherit'
    }
  }
})

interface DealSelectProps extends Omit<DropdownSelectProps, 'value'> {
  query?: string
  searchCompanyId?: string
  searchByName?: boolean
  value: string | string[]
  nameValue?: string
  onFetched?: (users: DropdownSelectOption[]) => void
}

export function DealSelect({
  value,
  nameValue,
  variant,
  query: propQuery,
  searchCompanyId,
  searchByName,
  ...props
}: DealSelectProps) {
  const classes = useStyles()
  const [options, setOptions] = useState<DropdownSelectOption[]>([])
  const { crmContext } = useCrmDialogs()
  const { deal: dealContext } = crmContext || {}
  const { search } = dealContext || {}
  const { postFilter, query: contextQuery } = search || {}
  const query = propQuery || contextQuery || ''

  const { loading, refetch, error, data } = useSearchDealsQuery({
    variables: {
      query,
      limit: 5,
      omitInvalidContacts: true
    },
    fetchPolicy: 'no-cache',
    skip: !!searchCompanyId
  })
  const {
    loading: companyLoading,
    refetch: companyRefetch,
    data: companyData,
    error: companyError
  } = useSearchDealsByCompanyQuery({
    variables: {
      query,
      limit: 5,
      companyId: searchCompanyId
    },
    fetchPolicy: 'no-cache',
    skip: !searchCompanyId
  })

  const mapDealsToOptions = (deals: Deal[]) => {
    return deals.map((deal) => ({
      ...deal,
      label: deal.name,
      value: deal._id
    }))
  }

  useEffect(() => {
    const deals = (data?.searchDeals?.deals as Deal[]) ?? (companyData?.searchDealsByCompany?.deals as Deal[]) ?? []
    const filteredDeals = postFilter ? deals.filter(postFilter) : deals
    setOptions(mapDealsToOptions(filteredDeals))
  }, [data, companyData])

  const selectedValues: DropdownSelectOption[] = options.filter((option) =>
    (Array.isArray(value) ? value : [value]).includes(option.value)
  )

  const singleSelectProps = {
    ...props,
    value: {
      ...selectedValues[0],
      label: nameValue || selectedValues[0]?.label || ''
    },
    variant: 'single'
  } as SingleSelectProps
  const multiSelectProps = { ...props, value: selectedValues, variant: 'multi' } as MultiSelectProps

  const selectProps = variant === 'multi' ? multiSelectProps : singleSelectProps

  const handleSearch = async (searchText: string) => {
    let deals: Deal[] = []
    const searchTerm = searchText.trim().length ? (searchByName ? `name:${searchText}*` : `${searchText}*`) : ''
    const searchQuery = searchTerm ? (query.length ? `${query} AND ${searchTerm}` : searchTerm) : query
    if (searchCompanyId) {
      const { data } = await companyRefetch({
        companyId: searchCompanyId,
        query: searchQuery
      })
      deals = (data?.searchDealsByCompany?.deals as Deal[]) ?? []
    } else {
      const { data } = await refetch({
        query: searchQuery
      })
      deals = (data?.searchDeals?.deals as Deal[]) ?? []
    }
    const filteredDeals = postFilter ? deals.filter(postFilter) : deals
    setOptions(mapDealsToOptions(filteredDeals))
  }
  const debouncedSearch = useDebouncedCallback(handleSearch, 200)

  return (
    <div style={{ width: '100%' }}>
      <DropdownSelect
        {...selectProps}
        disabled={props.disabled}
        loading={loading || companyLoading}
        options={options}
        searchCallback={(text) => debouncedSearch(text)}
      />
      {(error || companyError) && (
        <div className={classes.error}>
          <Typography variant="subtitle2">
            Unable to load deals.{' '}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault()
                refetch()
              }}
            >
              Retry?
            </a>
          </Typography>
        </div>
      )}
    </div>
  )
}

export default DealSelect
